import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { MeasurementPoint, Pallet, PressureFrame } from '../types/domain'
import { dateTime, number } from './format'
import { ForceMeasurementPanel } from './ForceMeasurementPanel'
import { PalletSearchSelect } from './PalletSearchSelect'
import { Pressure3D } from './Pressure3D'
import { formatPressure, type PressureUnit } from './pressureUnits'
import { StatusBadge } from './StatusBadge'
import { TimeSeriesChart } from './TimeSeriesChart'

export function PalletsView({ pallets }: { pallets: Pallet[] }) {
  const [selectedId, setSelectedId] = useState(() => pallets.find(p => p.measurementCount > 0)?.id ?? pallets[0]?.id ?? '')
  const [series, setSeries] = useState<MeasurementPoint[]>([])
  const [frame, setFrame] = useState<PressureFrame>()
  const [mode, setMode] = useState<'panel' | '3d' | '2d'>('panel')
  const [pressureUnit, setPressureUnit] = useState<PressureUnit>('KPA')
  const [loadFilter, setLoadFilter] = useState<'ALL' | 'NORMAL' | 'HIGH' | 'CRITICAL'>('ALL')
  const [filmBreakOnly, setFilmBreakOnly] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const filteredPallets = useMemo(() => pallets.filter(value => {
    const loadMatches = loadFilter === 'ALL'
      || (loadFilter === 'NORMAL' && value.peakLoadKpa > 0 && value.peakLoadKpa < 60)
      || (loadFilter === 'HIGH' && value.peakLoadKpa >= 60 && value.peakLoadKpa < 80)
      || (loadFilter === 'CRITICAL' && value.peakLoadKpa >= 80)
    return loadMatches && (!filmBreakOnly || value.filmBreakCount > 0)
  }), [filmBreakOnly, loadFilter, pallets])
  const pallet = pallets.find(value => value.id === selectedId)

  useEffect(() => {
    if (!selectedId) return
    let active = true
    setLoading(true)
    setError(undefined)
    Promise.all([api.series(selectedId), api.pressureFrame(selectedId)])
      .then(([nextSeries, nextFrame]) => {
        if (!active) return
        setSeries(nextSeries)
        setFrame(nextFrame)
      })
      .catch(errorValue => active && setError(errorValue instanceof Error ? errorValue.message : 'Ошибка загрузки'))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [selectedId])

  if (!pallet) return <div className="empty-state">Паллеты не найдены.</div>
  return (
    <section className="page-section">
      <div className="section-heading">
        <div><p className="eyebrow">Аналитика перевозки</p><h1>Нагрузка на паллету</h1></div>
        <p className="section-note">Найдите паллету по номеру, партии или грузу и ограничьте список по пиковой нагрузке.</p>
      </div>
      <div className="analytics-toolbar">
        <PalletSearchSelect pallets={filteredPallets} value={selectedId} onChange={setSelectedId} />
        <fieldset className="load-filters"><legend>Пиковая нагрузка</legend>
          {([['ALL', 'Все'], ['NORMAL', `< ${formatPressure(60, pressureUnit)}`], ['HIGH', `${formatPressure(60, pressureUnit)}–${formatPressure(80, pressureUnit)}`], ['CRITICAL', `≥ ${formatPressure(80, pressureUnit)}`]] as const).map(([value, label]) =>
            <button type="button" className={loadFilter === value ? 'active' : ''} key={value} onClick={() => setLoadFilter(value)}>{label}</button>)}
          <label><input type="checkbox" checked={filmBreakOnly} onChange={event => setFilmBreakOnly(event.currentTarget.checked)} /> Только с обрывом плёнки</label>
        </fieldset>
      </div>
      {filteredPallets.length === 0 ? <div className="filter-empty"><strong>Паллеты по выбранным условиям не найдены</strong><span>Измените диапазон нагрузки или отключите фильтр обрывов плёнки.</span></div> : <>
      <div className="pallet-summary">
        <div><span>Партия</span><strong>{pallet.batchNumber}</strong></div>
        <div><span>Груз</span><strong>{pallet.cargoType ?? '—'}</strong></div>
        <div><span>Масса</span><strong>{number(pallet.weightKg)} кг</strong></div>
        <div><span>Габариты</span><strong>{pallet.widthMm} × {pallet.depthMm} × {pallet.heightMm} мм</strong></div>
        <div><span>Статус</span><StatusBadge status={pallet.status} /></div>
        <div><span>Средняя нагрузка</span><strong>{pallet.averageLoadKpa ? formatPressure(pallet.averageLoadKpa, pressureUnit) : 'Нет данных'}</strong></div>
        <div><span>Пиковая нагрузка</span><strong className={pallet.peakLoadKpa >= 80 ? 'danger-text' : ''}>{pallet.peakLoadKpa ? formatPressure(pallet.peakLoadKpa, pressureUnit) : 'Нет данных'}</strong></div>
        <div><span>Обрывы плёнки</span><strong className={pallet.filmBreakCount ? 'danger-text' : ''}>{pallet.filmBreakCount || 'Не выявлены'}</strong></div>
      </div>
      <article className={`card visualization-card${mode === 'panel' ? ' visualization-card-panel' : ''}`}>
        <div className="card-header">
          <div><p className="eyebrow">{mode === 'panel' ? 'Измерительная панель' : mode === '3d' ? 'Пространственная схема' : 'Временной ряд'}</p><h2>{mode === 'panel' ? 'Профиль сил на упаковку' : mode === '3d' ? '3D-карта давления' : 'Динамика нагрузки'}</h2></div>
          <div className="visualization-controls">
            {mode !== '2d' && <div className="unit-switch" role="group" aria-label="Единицы давления">
              <button type="button" className={pressureUnit === 'KPA' ? 'active' : ''} onClick={() => setPressureUnit('KPA')}>кПа</button>
              <button type="button" className={pressureUnit === 'KGF_CM2' ? 'active' : ''} onClick={() => setPressureUnit('KGF_CM2')}>кгс/см²</button>
            </div>}
            <div className="segmented" aria-label="Режим графика"><button className={mode === 'panel' ? 'active' : ''} onClick={() => setMode('panel')}>Панель</button><button className={mode === '3d' ? 'active' : ''} onClick={() => setMode('3d')}>3D</button><button className={mode === '2d' ? 'active' : ''} onClick={() => setMode('2d')}>Время</button></div>
          </div>
        </div>
        {loading && <div className="chart-loader">Загрузка измерений…</div>}
        {error && <div className="inline-error">{error}</div>}
        {!loading && !error && mode === 'panel' && frame && <ForceMeasurementPanel pallet={pallet} frame={frame} points={series} unit={pressureUnit} />}
        {!loading && !error && mode === '3d' && frame && <Pressure3D pallet={pallet} frame={frame} unit={pressureUnit} />}
        {!loading && !error && mode === '2d' && <TimeSeriesChart key={pallet.id} points={series} palletNumber={pallet.number} />}
        <footer className="visualization-footer">
          <span>{mode === 'panel' ? 'Режим: профиль по высоте и времени' : 'Режим: угловые датчики, без скрытой интерполяции'}</span>
          <span>Момент: {dateTime(frame?.timestamp)}</span>
          <span>{series.length} точек после downsampling</span>
        </footer>
      </article>
      </>}
    </section>
  )
}
